import React, { useEffect, useState, useCallback, memo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CONFIG from '../../config';
import { getToken } from '../../utils/auth';

const WishlistAdmin = memo(() => {
    const { t, i18n } = useTranslation();
    const isAr = i18n.language?.startsWith('ar');
    const [data, setData] = useState({ products: [], totalUsers: 0 });
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const headers = { Authorization: `Bearer ${getToken()}` };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/wishlist/admin/insights`, { headers });
            setData(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []); // eslint-disable-line

    useEffect(() => { fetchData(); }, [fetchData]);

    const products = data.products || [];
    const maxCount = products[0]?.wishlist_count || 1;

    return (
        <div className="max-w-5xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
            <div className="flex items-center justify-between mb-10">
                <div>
                    <h1 className="text-4xl font-black text-gray-900 tracking-tight">Wishlist Insights</h1>
                    <p className="text-gray-400 text-sm font-medium mt-1">Most saved products by your customers</p>
                </div>
                <button
                    onClick={() => navigate("/admin")}
                    className="flex items-center gap-2 text-gray-500 hover:text-brand-primary transition-all text-sm font-medium group"
                >
                    <span className={`transition-transform group-hover:-translate-x-1 ${isAr ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                    {t('admin.common.back', 'Back')}
                </button>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4 mb-10">
                <div className="bg-red-50 border border-red-100 rounded-2xl p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-red-400 mb-1">Total Wishlisted Products</p>
                    <p className="text-3xl font-black text-red-700">{products.length}</p>
                </div>
                <div className="bg-pink-50 border border-pink-100 rounded-2xl p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-pink-400 mb-1">Users with Wishlists</p>
                    <p className="text-3xl font-black text-pink-700">{data.totalUsers}</p>
                </div>
            </div>

            {loading ? (
                <div className="p-16 text-center text-gray-300 animate-pulse font-bold">Loading...</div>
            ) : products.length === 0 ? (
                <div className="p-16 text-center">
                    <i className="far fa-heart text-5xl text-gray-100 mb-4 block" />
                    <p className="text-gray-300 font-bold">No wishlist data yet</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {products.map((product, idx) => (
                        <div key={product.id} className="flex items-center gap-4 p-4 rounded-2xl border border-gray-100 hover:border-red-100 hover:bg-red-50/30 transition-all group">
                            {/* Rank */}
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black ${idx < 3 ? 'bg-red-500 text-white shadow-lg shadow-red-200' : 'bg-gray-100 text-gray-400'}`}>
                                {idx + 1}
                            </div>
                            {/* Image */}
                            <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0">
                                <img src={product.imageUrl || '/placeholder-furniture.jpg'} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                            </div>
                            {/* Name */}
                            <div className="flex-1 min-w-0">
                                <p className="font-black text-gray-900 text-sm line-clamp-1">{isAr && product.name_ar ? product.name_ar : product.name}</p>
                                <p className="text-[10px] text-gray-400 font-bold">${parseFloat(product.price || 0).toFixed(2)}</p>
                            </div>
                            {/* Bar chart */}
                            <div className="flex-1 hidden md:block">
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-red-400 to-pink-500 rounded-full transition-all duration-700"
                                        style={{ width: `${(product.wishlist_count / maxCount) * 100}%` }}
                                    />
                                </div>
                            </div>
                            {/* Count badge */}
                            <div className="flex items-center gap-1.5 bg-red-50 border border-red-100 px-3 py-1.5 rounded-xl">
                                <i className="fas fa-heart text-red-400 text-xs" />
                                <span className="text-sm font-black text-red-700">{product.wishlist_count}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export default WishlistAdmin;
